import React, { useCallback, useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import { message, Spin } from "antd";
import styles from "./index.module.less";
import SearchInput from "@components/searchInput";
import { useChatStore } from "@store/chatStore";
import { useReferenceStore } from "@store/referenceStore";
import { useSSENoticeStore } from "@store/sseNoticeStore";
import MessageCard from "./components/MessageCard";
import { createMockUserMessage, createMockBotMessage } from "./utils";
import { useAutoScrollToBottom } from "@hooks/useAutoScrollToBottom";
import { useScrollLoadMore } from "@hooks/useScrollLoadMore";
import { useSSEConversation, useHistoryMessages, useMarkdownFeedback, useReferenceFeedback } from "./hooks";
import { usePolling } from "@hooks/usePolling";
import type { ToolClickAction } from "./types";
import { ConversationSceneType } from "@typing/wsui/msg";
import type { IMessage } from "@typing/wsui/getMessages";
import cx from "classnames";
// import CustomError from "@components/customError";
import ReferenceList from "./components/ReferenceList";
import { useUbcSend } from "@hooks/useUbcSend";
import { useHeguiNavigate } from "@hooks/useHeguiNavigate";

/**
 * 组件卸载时关闭引用文献抽屉的 Hook
 *
 * 注意：由于路由配置中使用了 <Chat key={sessionId} />，
 * 当 sessionId 变化时组件会完全重新挂载，无法在同一实例中检测到 sessionId 变化。
 * 因此，我们在组件卸载时关闭抽屉，确保切换会话时抽屉被正确关闭。
 *
 * @param closeDrawer 关闭抽屉的函数
 */
const useCloseDrawerOnUnmount = (closeDrawer: () => void) => {
    useEffect(() => {
        // 返回清理函数，在组件卸载时执行
        return () => {
            console.log("🔄 组件卸载，自动关闭引用文献抽屉");
            closeDrawer();
        };
    }, [closeDrawer]);
};

/**
 * 监听 SSE 通知自动打开引用文献抽屉的 Hook
 *
 * 功能：
 * - 监听 sseNoticeStore 中的 referencesReady 通知
 * - 同一个 messageId 只会自动打开一次抽屉
 * - 不同的 messageId 都可以各自触发一次自动打开
 *
 * @param effectiveSessionId 当前会话 ID
 * @param openDrawer 打开抽屉的函数
 */
const useAutoOpenDrawerOnReferencesReady = (
    effectiveSessionId: string,
    openDrawer: (sessionId: string, messageId: string) => void
) => {
    // 记录已自动打开过抽屉的 messageId 集合
    const openedMessageIdsRef = useRef<Set<string>>(new Set());
    const { notice, clearNotice } = useSSENoticeStore();

    useEffect(() => {
        if (notice?.type === "referencesReady") {
            const { sessionId: noticeSessionId, messageId } = notice;
            // 确保是当前会话的通知，且该 messageId 未自动打开过
            if (noticeSessionId === effectiveSessionId && !openedMessageIdsRef.current.has(messageId)) {
                openDrawer(noticeSessionId, messageId);
                openedMessageIdsRef.current.add(messageId);
            }
            // 消费通知后清除
            clearNotice();
        }
    }, [notice, effectiveSessionId, openDrawer, clearNotice]);
};

// 注意：由于路由配置中使用了 <Chat key={sessionId} />
// sessionId 变化时组件会自动重新挂载，所有状态自动重置
const ChatPage: React.FC = () => {
    const { sessionId, shareId } = useParams<{
        sessionId?: string;
        shareId?: string;
    }>();
    const { ubcSend } = useUbcSend();
    const {
        pendingQuery,
        clearPendingQuery,
        getSession,
        setCurrentSession,
        addMessage: storeAddMessage,
        updateMessage: storeUpdateMessage,
        removeMessage: storeRemoveMessage,
        setPageTitle: storeSetPageTitle,
        initSession,
        notifyNewSession,
        notifySessionTitleUpdate,
    } = useChatStore();

    useEffect(() => {
        // 后端返回后才会有 sessionid, 没有 sessionid 先不上报
        if (!sessionId) return;
        ubcSend({
            type: "view",
            value: "page",
            ext: {
                product_info: {
                    sessionId,
                },
            },
        });
    }, [sessionId, ubcSend]);
    // 用于新会话的临时 sessionId
    const [tempSessionId, setTempSessionId] = useState<string>("");

    // 计算实际使用的 sessionId：优先使用 URL 中的，其次使用临时的
    const effectiveSessionId = sessionId || shareId || tempSessionId || "";

    // 从 store 读取 pageTitle
    const sessionData = getSession(effectiveSessionId);
    const pageTitle = sessionData?.pageTitle || "对话详情";

    // 从 store 读取思维链展开状态
    const { toggleExpandedThinking } = useChatStore();
    // 使用稳定的空对象引用，避免 || {} 每次创建新对象导致无限循环
    const emptyExpandedThinking = useMemo(() => ({}), []);
    const expandedThinking = useChatStore(
        useCallback(
            (state) => state.sessions[effectiveSessionId]?.expandedThinking || emptyExpandedThinking,
            [effectiveSessionId, emptyExpandedThinking]
        )
    );

    // 初始化相关 refs
    const initializingRef = useRef(false);
    const isNewConversationRef = useRef(false);

    // 历史消息 Hook（简化参数，数据从 store 读取）
    const { messages, loading, loadingMore, hasMore, error, setError, loadHistoryMessages, loadMoreMessages } =
        useHistoryMessages({
            sessionId: effectiveSessionId || sessionId,
            shareId,
        });

    // SSE 对话 Hook（简化参数，数据写入 store）
    const { startConversation, closeClient, sseLoading } = useSSEConversation({
        sessionId: effectiveSessionId,
        onError: setError,
    });

    // Polling：进入 chat 页面后启动，直到页面关闭
    const pollingStartedRef = useRef(false);
    const { startPolling } = usePolling({
        sessionId: effectiveSessionId,
        onNewMessages: useCallback(
            (msgs: IMessage[]) => {
                msgs.filter((msg: IMessage) => msg.poster?.role !== "user").forEach((msg: IMessage) =>
                    storeAddMessage(effectiveSessionId, msg)
                );
            },
            [effectiveSessionId, storeAddMessage]
        ),
    });

    useEffect(() => {
        if (!pollingStartedRef.current && sessionId) {
            pollingStartedRef.current = true;
            startPolling();
        }
    }, [sessionId, startPolling]);

    // 初始化时同步当前会话
    useEffect(() => {
        setCurrentSession(sessionId || null);
    }, [sessionId, setCurrentSession]);

    // 滚动加载更多 Hook（滚动到顶部时加载历史消息）
    const { setContainer: setScrollLoadContainer } = useScrollLoadMore({
        hasMore,
        loading: loadingMore,
        threshold: 5,
        onLoadMore: loadMoreMessages,
        messages,
        timeout: 6000,
    });

    // 自动滚动到底部（新消息时）
    const { containerRef: autoScrollContainerRef, resetScroll } = useAutoScrollToBottom<HTMLDivElement>([messages], {
        enabled: !loading && messages.length > 0,
    });

    // 合并两个 callback ref
    const messagesContainerRef = useCallback(
        (node: HTMLDivElement | null) => {
            // 调用自动滚动的 ref
            autoScrollContainerRef(node);
            // 同步给滚动加载 Hook
            setScrollLoadContainer(node);
        },
        [autoScrollContainerRef, setScrollLoadContainer]
    );

    /**
     * 初始化页面
     */
    useEffect(() => {
        // 防止重复初始化
        if (initializingRef.current) {
            console.log("⏭️ 正在初始化中，跳过");
            return;
        }
        initializingRef.current = true;

        console.log("🔍 initializePage 被调用", {
            sessionId,
            shareId,
            pendingQuery,
        });

        // 新会话路径 A：已有 sessionId（来自 coredata）但带 pendingQuery → 用该 sessionId 直接开始
        if (sessionId && pendingQuery && !isNewConversationRef.current) {
            console.log("✅ 识别为带 sessionId 的新会话（coredata 流程）", { sessionId, pendingQuery });
            isNewConversationRef.current = true;

            const userMsg = createMockUserMessage(pendingQuery.query, pendingQuery.images);
            const botMsg = createMockBotMessage();

            const imagePrefix = pendingQuery.images?.length ? "[图片]".repeat(pendingQuery.images.length) : "";
            const displayTitle = imagePrefix + (pendingQuery.query || "") || "新会话";
            initSession(sessionId);
            storeAddMessage(sessionId, userMsg);
            storeAddMessage(sessionId, botMsg);
            storeSetPageTitle(sessionId, displayTitle);
            notifyNewSession(sessionId);
            notifySessionTitleUpdate(sessionId, displayTitle);

            const query = pendingQuery.query;
            const images = pendingQuery.images;
            clearPendingQuery();
            startConversation(
                query,
                sessionId,
                undefined,
                userMsg.message_id,
                botMsg.message_id,
                pendingQuery.sceneType,
                undefined,
                images
            );
            return;
        }

        // 新会话路径 B：没有 sessionId，有 pendingQuery
        if (!sessionId && !shareId && !isNewConversationRef.current) {
            if (pendingQuery) {
                console.log("✅ 识别为新会话，启动 SSE 对话", { pendingQuery });
                isNewConversationRef.current = true;

                const userMsg = createMockUserMessage(pendingQuery.query, pendingQuery.images);
                const botMsg = createMockBotMessage();

                const imagePrefix = pendingQuery.images?.length ? "[图片]".repeat(pendingQuery.images.length) : "";
                const displayTitle = imagePrefix + (pendingQuery.query || "") || "新会话";
                // 新会话暂时用临时 ID，SSE 返回真实 session_id 后会更新
                const newTempSessionId = `temp-${Date.now()}`;
                setTempSessionId(newTempSessionId); // 更新组件状态
                initSession(newTempSessionId);
                storeAddMessage(newTempSessionId, userMsg);
                storeAddMessage(newTempSessionId, botMsg);
                storeSetPageTitle(newTempSessionId, displayTitle);
                notifySessionTitleUpdate(newTempSessionId, displayTitle);

                const query = pendingQuery.query;
                const images = pendingQuery.images;
                clearPendingQuery();
                // 将用户消息 ID 和 bot 消息 ID 传入 SSE
                startConversation(
                    query,
                    "",
                    newTempSessionId,
                    userMsg.message_id,
                    botMsg.message_id,
                    pendingQuery.sceneType,
                    undefined,
                    images
                );
            } else {
                initializingRef.current = false;
            }
            return;
        }

        // 已标记为新会话，跳过
        if (isNewConversationRef.current) {
            console.log("⏭️ 已是新会话，跳过历史消息加载");
            initializingRef.current = false;
            return;
        }

        // 历史会话：加载消息
        loadHistoryMessages();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionId, shareId]);

    // Markdown 反馈 Hook
    const { handleMarkdownFeedback, handleMarkdownCancel } = useMarkdownFeedback({
        sessionId: sessionId || "",
        effectiveSessionId,
    });

    // 引用文献反馈 Hook
    const { handleReferenceFeedback, handleReferenceCancel } = useReferenceFeedback({
        sessionId: sessionId || "",
        effectiveSessionId,
    });

    const {
        visible: drawerVisible,
        currentSegment,
        messageId: currentMessageId,
        anchorIndex,
        paragraphIndex,
        triggerTimestamp,
        closeDrawer,
        openDrawer,
    } = useReferenceStore();

    // 合规校验跳转 hook
    const { navigate: heguiNavigate } = useHeguiNavigate();

    // 监听 SSE 通知，自动打开引用文献抽屉
    useAutoOpenDrawerOnReferencesReady(effectiveSessionId, openDrawer);

    // 组件卸载时关闭引用文献抽屉（用于会话切换场景）
    useCloseDrawerOnUnmount(closeDrawer);

    // 切换思考过程展开/收起（使用 store 方法）
    const toggleThinking = useCallback(
        (segmentKey: string) => {
            toggleExpandedThinking(effectiveSessionId, segmentKey);
        },
        [effectiveSessionId, toggleExpandedThinking]
    );

    /**
     * 工具按钮点击处理（路由层）
     * 根据 type 和 source 字段路由到对应的 Hook
     */
    const handleToolClick = useCallback(
        async (action: ToolClickAction): Promise<{ success: boolean } | undefined> => {
            if (action.type === "copy") {
                if (navigator.clipboard) {
                    navigator.clipboard.writeText(action.content);
                } else {
                    const textarea = document.createElement("textarea");
                    textarea.value = action.content;
                    textarea.style.position = "fixed";
                    textarea.style.opacity = "0";
                    document.body.appendChild(textarea);
                    textarea.select();
                    document.execCommand("copy");
                    document.body.removeChild(textarea);
                }
                message.success("已复制到剪贴板");
                return undefined;
            }

            // Markdown 反馈处理
            if (action.type === "cancel") {
                return await handleMarkdownCancel(action.params);
            }
            return await handleMarkdownFeedback(action.type, action.params);
        },
        [handleMarkdownFeedback, handleMarkdownCancel]
    );

    // 发送消息
    const handleSubmit = useCallback(
        (
            value: string,
            images?: any[],
            sceneType: ConversationSceneType = ConversationSceneType.UserInput
        ): Promise<void> => {
            if (!value.trim() && (!images || images.length === 0)) return Promise.resolve();

            // 构建消息图片数据
            const messageImages = images?.map((img: any) => ({
                fileName: img.fileName,
                icon: img.iconUrl || img.url,
                origin: img.uploadedUrl || img.url,
                small: img.smallUrl || img.url,
            }));

            const userMsg = createMockUserMessage(value, messageImages);
            const botMsg = createMockBotMessage();

            // 写入 store
            storeAddMessage(effectiveSessionId, userMsg);
            storeAddMessage(effectiveSessionId, botMsg);

            resetScroll();

            // 确定 sceneType：有图片时使用 InputImg
            const finalSceneType = images && images.length > 0 ? ConversationSceneType.InputImg : sceneType;

            // 传入用户消息 ID 和 bot 消息 ID，用于 SSE 更新真实 message_id
            startConversation(
                value,
                sessionId || "",
                undefined,
                userMsg.message_id,
                botMsg.message_id,
                finalSceneType,
                undefined,
                messageImages
            );
            return Promise.resolve();
        },
        [sessionId, effectiveSessionId, startConversation, storeAddMessage, resetScroll]
    );

    const handleRetryMessage = useCallback(
        (failedMessage: IMessage) => {
            if (sseLoading) {
                message.warning('对话进行中，请稍后再试');
                return;
            }
            const value = failedMessage.sections[0]?.segments[0]?.content || '';
            const images = failedMessage.images;
            if (!value.trim() && (!images || images.length === 0)) return;

            const currentMessages = getSession(effectiveSessionId)?.messages || [];
            const failedMessageIndex = currentMessages.findIndex((msg) => msg.message_id === failedMessage.message_id);
            const nextMessage = failedMessageIndex > -1 ? currentMessages[failedMessageIndex + 1] : undefined;
            if (nextMessage?.poster.role === 'bot' && nextMessage.isLoading) {
                storeRemoveMessage(effectiveSessionId, nextMessage.message_id);
            }

            storeUpdateMessage(effectiveSessionId, failedMessage.message_id, (msg) => ({
                ...msg,
                sendFailed: false,
            }));

            const botMsg = createMockBotMessage();
            storeAddMessage(effectiveSessionId, botMsg);
            resetScroll();

            startConversation(
                value,
                sessionId || '',
                undefined,
                failedMessage.message_id,
                botMsg.message_id,
                images && images.length > 0 ? ConversationSceneType.InputImg : ConversationSceneType.UserInput,
                undefined,
                images
            );
        },
        [
            effectiveSessionId,
            getSession,
            resetScroll,
            sessionId,
            sseLoading,
            startConversation,
            storeAddMessage,
            storeRemoveMessage,
            storeUpdateMessage,
        ]
    );

    /**
     * 处理追问点击
     * 直接触发发送逻辑，在同一 session 内重新提问
     */
    const handleAppendAskClick = useCallback(
        (question: string) => {
            if (!question.trim()) return;
            if (sseLoading) {
                message.warning("对话进行中，请稍后再试");
                return;
            }
            // 直接调用现有的发送逻辑
            handleSubmit(question, undefined, ConversationSceneType.QuickReplySelector);
        },
        [handleSubmit, sseLoading]
    );

    // 错误状态 - toast提示
    useEffect(() => {
        if (error) {
            message.error(error || '加载失败，请重试');
        }
    }, [error]);

    // 加载中状态
    if (loading) {
        return (
            <div className={styles.loadingContainer}>
                <Spin></Spin>
            </div>
        );
    }

    return (
        <div
            className={cx(styles.box, "relative", {
                [styles.boxWithDrawer]: drawerVisible,
            })}
        >
            {/* 顶部标题栏 */}
            <header className={styles.header}>
                <div className={cx(styles.title, "ellipsis")}>{pageTitle}</div>
            </header>
            <div className={cx(styles.contentWrapper, "relative")}>
                <div className={styles.content} ref={messagesContainerRef}>
                    <div className={styles.historyPage}>
                        {/* 对话区域 */}
                        <div className={styles.messagesContainer}>
                            <div className={styles.messagesList}>
                                {/* 加载更多提示（自动触发） */}
                                {loadingMore && (
                                    <div className={styles.loadingMoreIndicator}>
                                        <Spin></Spin>
                                    </div>
                                )}
                                {/* 消息列表 */}
                                {messages.map((msg, index) => (
                                    <div
                                        key={msg.message_id || index}
                                        data-message-id={msg.message_id}
                                        className={index === 0 ? styles.firstMessage : undefined}
                                    >
                                        <MessageCard
                                            message={msg}
                                            expandedThinking={expandedThinking}
                                            onToggleThinking={toggleThinking}
                                            onToolClick={handleToolClick}
                                            sessionId={effectiveSessionId}
                                            onAppendAskClick={handleAppendAskClick}
                                            onRetryMessage={handleRetryMessage}
                                            prevSameRole={
                                                index > 0 && messages[index - 1]?.poster?.role === msg.poster?.role
                                            }
                                            sseLoading={sseLoading}
                                        />
                                    </div>
                                ))}

                                {/* AI 消息 loading 占位：当 SSE 加载中且最后一条是用户消息时显示 */}
                                {sseLoading &&
                                    messages.length > 0 &&
                                    messages[messages.length - 1]?.poster?.role === "user" && (
                                        <MessageCard
                                            message={{
                                                message_id: "loading-placeholder",
                                                poster: { name: "AI助手", avatar: "", role: "bot" },
                                                sections: [],
                                                isLoading: true,
                                            }}
                                            expandedThinking={{}}
                                            onToggleThinking={() => {}}
                                            onToolClick={handleToolClick}
                                            sessionId={effectiveSessionId}
                                            onAppendAskClick={handleAppendAskClick}
                                        />
                                    )}

                                {/* 空状态 */}
                                {messages.length === 0 && <div className={styles.emptyState}>暂无消息</div>}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 底部输入框 */}
            <div className={styles.inputArea}>
                <div className={styles.inputGradient} />
                <div className={styles.inputWrapper}>
                    <SearchInput
                        placeholder="请输入你的医学问题..."
                        minHeight={118}
                        maxHeight={299}
                        onSubmit={handleSubmit}
                        onStop={closeClient}
                        sseLoading={sseLoading}
                    />
                </div>
            </div>

            {/* 引用文献抽屉 */}
            <ReferenceList
                visible={drawerVisible}
                segment={currentSegment}
                messageId={currentMessageId}
                anchorIndex={anchorIndex}
                paragraphIndex={paragraphIndex}
                triggerTimestamp={triggerTimestamp}
                onClose={closeDrawer}
                onFeedback={handleReferenceFeedback}
                onCancelFeedback={handleReferenceCancel}
                onNavigate={heguiNavigate}
                sseLoading={sseLoading}
            />
        </div>
    );
};

export default ChatPage;
